import { useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresAdmin?: boolean;
  requiresFounder?: boolean;
}

/**
 * ProtectedRoute - Guards routes that require authentication.
 *
 * IMPORTANT: Once a user session is established, children are kept mounted even
 * during subsequent loading states (e.g., token refresh, claims reload). This
 * prevents state loss in components like OnboardingFlow that maintain internal
 * step state. The loading overlay is shown on top of children instead of replacing them.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiresAdmin = false,
  requiresFounder = false
}) => {
  const { user, loading, profile, isFounder } = useAuth();

  // Track if we've ever had a valid user session
  // Once true, we keep children mounted even during loading to preserve state
  const hasHadUserRef = useRef(false);
  if (user) {
    hasHadUserRef.current = true;
  }

  // Initial loading before any user session - show full-screen spinner
  // This only applies on first load, not during token refreshes
  if (loading && !hasHadUserRef.current) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-slate-300 font-medium">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  // No user and done loading - redirect to login
  if (!user && !loading) {
    return <Navigate to="/login" replace />;
  }

  // During claim/profile refreshes for an existing session, keep children mounted.
  // This avoids transient redirects on guarded routes while role claims are still loading.
  if (loading && user) {
    return <>{children}</>;
  }

  // Check for Coordi Team founder access
  if (requiresFounder && !isFounder) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check for admin access
  if (requiresAdmin && profile?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Render children (keep them mounted even during loading to preserve state)
  // If loading during an existing session, children stay mounted - any loading
  // indicators should be handled by the child components themselves
  return <>{children}</>;
};
